import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select from "react-select";

import OfferAPI from "../../../api/OfferAPI";
import { alphaSortBy } from "../../../utils/sort";
import Button from "../../button/Button";
import RelatedList from "../../utils/RelatedList";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type {
  MediaOffer,
  MediaOfferAdded,
  MediaUpdated,
} from "../../../api/MediaAPI.types";
import type { AppState } from "../../../reducers";

interface FormatedOffer {
  label: string;
  uuid: string;
  url: string;
  hasAds: boolean;
}

interface OffersByProject {
  items: FormatedOffer[];
  title: string;
  uuid: string;
}

interface IOffersList {
  offersByProject: Record<string, OffersByProject>;
  deleteOfferOnMedia: (uuid: string) => void;
}

function OffersList({
  offersByProject,
  deleteOfferOnMedia,
}: IOffersList): JSX.Element {
  const { t } = useTranslation("editor-content");

  const offersArray = alphaSortBy(Object.values(offersByProject), "title");

  if (offersArray.length === 0) {
    return <span>{t("offerEmpty")}</span>;
  }

  return (
    <div className="flex flex-col gap-[0.9375rem]">
      {offersArray.map((offerByProject) => (
        <RelatedList
          key={offerByProject.uuid}
          items={offerByProject.items}
          onDelete={deleteOfferOnMedia}
          title={offerByProject.title}
        />
      ))}
    </div>
  );
}

interface Option {
  label: string;
  value: string;
  isDisabled?: boolean;
}

interface IAddOffer {
  addOfferOnMedia: (offer: MediaOfferAdded) => void;
  offersByProject: Record<string, OffersByProject>;
}

function AddOffer({
  addOfferOnMedia,
  offersByProject,
}: IAddOffer): JSX.Element {
  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );
  const isSingleProject = projects?.data?.length === 1;
  const [selectedProject, setSelectedProject] = React.useState<Option | null>(
    isSingleProject && projects?.data[0]
      ? {
          label: projects?.data[0].name,
          value: projects?.data[0].uuid,
        }
      : null,
  );

  const [selectedOffer, setSelectedOffer] = React.useState<Option | null>(null);

  const getOffersQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getOffers,
    params: [
      {
        projectSlug: selectedProject?.value ?? "",
      },
    ],
  });

  const { data: offers, isLoading } = useQuery({
    ...getOffersQuery,
    enabled: !!selectedProject,
  });

  const { t } = useTranslation("editor-content");

  const handleChangeProject = (option: SingleValue<Option>): void => {
    if (option) {
      setSelectedProject(option);
    } else {
      setSelectedProject(null);
      setSelectedOffer(null);
    }
  };

  const handleChangeOffer = (option: SingleValue<Option>): void => {
    setSelectedOffer(option);
  };

  const projectOptions = projects?.data.map((project) => ({
    label: project.name,
    value: project.uuid,
  }));

  const offerOptions = offers?.data.map((offer) => {
    const isDisabled = Boolean(
      selectedProject &&
      offersByProject[selectedProject?.label]?.items?.some(
        (offerExist) => offerExist.uuid === offer.uuid,
      ),
    );

    return {
      label: offer.label,
      value: offer.uuid,
      isDisabled,
    };
  });

  const addOffer = (): void => {
    if (selectedProject && selectedOffer) {
      addOfferOnMedia({
        uuid: selectedOffer.value,
        o_label: selectedOffer.label,
        storefront_project_name: selectedProject.label,
        storefront_project_uuid: selectedProject.value,
      });
      setSelectedProject(null);
      setSelectedOffer(null);
    }
  };

  return (
    <div className="flex gap-[0.46875rem]">
      <Select
        isClearable
        className="editor-select basis-1/3"
        classNamePrefix="slct"
        onChange={handleChangeProject}
        options={projectOptions}
        placeholder={t("selectProject")}
        value={selectedProject}
      />
      <Select
        isClearable
        className="editor-select basis-1/3"
        classNamePrefix="slct"
        isDisabled={!selectedProject}
        isLoading={Boolean(isLoading && selectedProject)}
        noOptionsMessage={(): string => t("noOffer")}
        onChange={handleChangeOffer}
        options={offerOptions}
        placeholder={t("selectOffer")}
        value={selectedOffer}
      />
      <Button
        className="bg-muted h-[36px] px-[1.25rem] py-[0.15625rem] text-[0.703125rem]"
        isDisabled={!selectedProject || !selectedOffer}
        onClick={addOffer}
        type="button"
      >
        {t("add")}
      </Button>
    </div>
  );
}

interface IRelatedOffers {
  offers: MediaOffer[];
  onUpdateOffersMedia: (offers: MediaUpdated["offers"]) => void;
}

function RelatedOffers({
  offers,
  onUpdateOffersMedia,
}: IRelatedOffers): JSX.Element {
  const offersByProject = offers.reduce(
    (acc: Record<string, OffersByProject>, value) => {
      const offer: FormatedOffer = {
        label: value.o_label,
        uuid: value.uuid,
        url: `/projects/${value.storefront_project_name}/offers/edit/${value.uuid}`,
        hasAds: value.has_ads,
      };

      const currentProject = acc[value.storefront_project_name];

      if (!currentProject?.items) {
        acc[value.storefront_project_name] = {
          title: value.storefront_project_name,
          items: [offer],
          uuid: value.storefront_project_uuid,
        };
      } else {
        acc[value.storefront_project_name] = {
          ...currentProject,
          items: [...currentProject.items, offer],
        };
      }
      return acc;
    },
    {},
  );

  const addOfferOnMedia = (offer: MediaOfferAdded): void => {
    onUpdateOffersMedia([...offers, offer]);
  };

  const deleteOfferOnMedia = (uuid: string): void => {
    const updatedOffers = offers.filter(
      (offerExist) => offerExist.uuid !== uuid,
    );
    onUpdateOffersMedia(updatedOffers);
  };

  return (
    <div className="flex flex-col gap-[1.5625rem]">
      <OffersList
        deleteOfferOnMedia={deleteOfferOnMedia}
        offersByProject={offersByProject}
      />
      <AddOffer
        addOfferOnMedia={addOfferOnMedia}
        offersByProject={offersByProject}
      />
    </div>
  );
}

export default RelatedOffers;
